// HeroCardiograph — the focal SVG trace.
// State drives waveform shape (cardiographMotion.ts) and colour (tokens.ts).
// RAF loop pushes one sample every MOTION.cardioTick ms; rolling buffer of
// MOTION.cardioPoints points produces the trace path.
//
// prefers-reduced-motion: reduce -> static pulsing dot instead of trace.

import { motion, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import {
  MID_Y,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  nextSample,
} from './cardiographMotion'
import { CARDIO_COLOR, CARDIO_GLOW, MOTION } from './tokens'
import type { CardiographState } from './types'

interface Props {
  state: CardiographState
  /** Optional pull progress 0..1 — rendered as bar below trace during 'pulling' */
  pullProgress?: number
  /** Optional metadata text shown below trace (e.g. "47% / 312 MB") */
  meta?: string
  height?: number
}

export function HeroCardiograph({ state, pullProgress, meta, height = 160 }: Props) {
  const prefersReduced = useReducedMotion()
  const colour = CARDIO_COLOR[state]
  const glow = CARDIO_GLOW[state]

  if (prefersReduced) {
    return (
      <ReducedMotionFallback state={state} colour={colour} meta={meta} height={height} />
    )
  }

  return (
    <div style={{ position: 'relative', width: '100%', height, userSelect: 'none' }}>
      <CardiographTrace state={state} colour={colour} glow={glow} />
      {state === 'pulling' && pullProgress !== undefined && (
        <PullBar progress={pullProgress} colour={colour} />
      )}
      {meta && (
        <motion.div
          key={meta}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={MOTION.viewSwap}
          style={{
            position: 'absolute',
            right: 12,
            bottom: 6,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            color: 'var(--ai-text-secondary, #5f7882)',
            letterSpacing: '0.02em',
          }}
        >
          {meta}
        </motion.div>
      )}
    </div>
  )
}

// ── Trace SVG with rolling buffer ──────────────────────────────────────

function CardiographTrace({
  state,
  colour,
  glow,
}: {
  state: CardiographState
  colour: string
  glow: number
}) {
  const tickRef = useRef(0)
  const lastStateRef = useRef<CardiographState>(state)
  const bufferRef = useRef<number[]>(Array(MOTION.cardioPoints).fill(MID_Y))
  const [, force] = useState(0)

  // Reset tick counter on state change so phase waveforms start crisp
  useEffect(() => {
    if (lastStateRef.current !== state) {
      tickRef.current = 0
      lastStateRef.current = state
    }
  }, [state])

  useEffect(() => {
    let raf: number
    let last = performance.now()

    const loop = (t: number) => {
      if (t - last >= MOTION.cardioTick) {
        last = t
        tickRef.current += 1
        const y = nextSample({ state: lastStateRef.current, tick: tickRef.current })
        const buf = bufferRef.current
        buf.shift()
        buf.push(y)
        force(n => (n + 1) & 0xff)
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [])

  const path = bufferToPath(bufferRef.current)

  return (
    <motion.svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {/* Baseline grid line */}
      <line
        x1={0}
        x2={VIEW_WIDTH}
        y1={MID_Y}
        y2={MID_Y}
        stroke={colour}
        strokeWidth={0.5}
        strokeDasharray="2 4"
        opacity={0.2}
      />
      {/* Trace */}
      <motion.path
        d={path}
        fill="none"
        stroke={colour}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        animate={{
          stroke: colour,
          filter: glow > 0 ? `drop-shadow(0 0 ${glow}px ${colour})` : 'none',
        }}
        transition={MOTION.rampSpring}
      />
    </motion.svg>
  )
}

function bufferToPath(buf: number[]): string {
  if (buf.length === 0) return ''
  const dx = VIEW_WIDTH / (buf.length - 1)
  let d = `M 0 ${buf[0].toFixed(2)}`
  for (let i = 1; i < buf.length; i++) {
    d += ` L ${(i * dx).toFixed(2)} ${buf[i].toFixed(2)}`
  }
  return d
}

// ── Pull progress bar (only during 'pulling' phase) ────────────────────

function PullBar({ progress, colour }: { progress: number; colour: string }) {
  const pct = Math.max(0, Math.min(1, progress)) * 100
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 3,
        background: 'rgba(0,0,0,0.06)',
      }}
    >
      <motion.div
        initial={false}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{
          height: '100%',
          background: colour,
          boxShadow: `0 0 8px ${colour}`,
        }}
      />
    </div>
  )
}

// ── Reduced-motion fallback ─────────────────────────────────────────────

function ReducedMotionFallback({
  state,
  colour,
  meta,
  height,
}: {
  state: CardiographState
  colour: string
  meta?: string
  height: number
}) {
  const isActive = state !== 'failed' && state !== 'stopped' && state !== 'idle'
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flexDirection: 'column',
      }}
    >
      <motion.div
        animate={isActive ? { scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] } : { scale: 1, opacity: 0.6 }}
        transition={isActive ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: colour,
        }}
      />
      {meta && (
        <span
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            color: 'var(--ai-text-secondary, #5f7882)',
          }}
        >
          {meta}
        </span>
      )}
    </div>
  )
}
